/**
 * components/help/HelpArticle.jsx — Phase 84.15
 *
 * Renders the current article body via react-markdown + remark-gfm.
 * When the viewer is a SuperAdmin, an inline "Edit" button appears on
 * every article; clicking it swaps the rendered body for a markdown
 * textarea + Save/Cancel. Saves via PATCH /api/help/articles/{slug}.
 */
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Pencil, Save, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { patchArticle } from '@/lib/helpApi';

function FormattedDate({ iso }) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return <>{d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</>;
  } catch { return null; }
}

export default function HelpArticle({ article, isSuperadmin, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');

  const startEdit = () => {
    setTitle(article.title || '');
    setSummary(article.summary || '');
    setBody(article.body_markdown || '');
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    if (!title.trim()) { toast.error('Title is required.'); return; }
    setSaving(true);
    try {
      const updated = await patchArticle(article.slug, {
        title: title.trim(),
        summary: summary.trim() || null,
        body_markdown: body,
      });
      toast.success('Saved.');
      setEditing(false);
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (!article) return null;

  return (
    <article className="bg-white rounded-2xl border border-border p-6 md:p-8" data-testid="help-article">
      {!editing ? (
        <>
          <header className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-foreground" data-testid="help-article-title">
                {article.title}
              </h1>
              {article.summary ? (
                <p className="text-sm text-muted-foreground mt-2 max-w-prose">{article.summary}</p>
              ) : null}
            </div>
            {isSuperadmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={startEdit}
                className="shrink-0 rounded-xl"
                data-testid="help-edit-button"
              >
                <Pencil className="h-4 w-4 mr-1.5" /> Edit
              </Button>
            )}
          </header>

          <div className="prose prose-sm md:prose-base max-w-none prose-headings:font-display prose-headings:text-foreground prose-p:text-foreground/85 prose-a:text-primary prose-strong:text-foreground prose-code:text-primary prose-code:bg-primary/8 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none" data-testid="help-article-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body_markdown || ''}</ReactMarkdown>
          </div>

          {article.updated_at ? (
            <footer className="mt-8 pt-4 border-t border-border/40 text-[11px] text-muted-foreground">
              Last updated <FormattedDate iso={article.updated_at} />
            </footer>
          ) : null}
        </>
      ) : (
        <>
          <header className="mb-4 flex items-center justify-between gap-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-primary">Editing (Superadmin)</div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={cancel} disabled={saving} className="rounded-xl" data-testid="help-edit-cancel">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={saving} className="rounded-xl" data-testid="help-edit-save">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </div>
          </header>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="help-edit-title" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Summary (optional)</label>
              <Input value={summary} onChange={(e) => setSummary(e.target.value)} className="mt-1" data-testid="help-edit-summary" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Body (markdown)</label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={22}
                className="mt-1 font-mono text-[13px] leading-relaxed"
                data-testid="help-edit-body"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Supports GitHub-flavoured markdown: **bold**, _italics_, `code`, [links](/help/...), lists, tables.
              </div>
            </div>
          </div>
        </>
      )}
    </article>
  );
}
